<template>
  <div @mouseup="handleMouseUp">
    <!-- Bot message -->
    <FloatingCard v-if="message.isBot">
      <BotMessageHeader
        :dateTime="message.dateTime?.toString()"
        :title="botMessageTitle"
      />
      <MarkDownComponent :message="message" />
      <slot v-if="botMessageFooter">
        <component :is="botMessageFooter(message)" />
      </slot>
      <ConversationActions
        v-if="!props.hideActions"
        :isAllowedClipboard="isAllowedClipboard"
        @action="onIconClick"
      />
      <CollapsibleSections
        v-if="shouldShowSections && orderedSections.length"
        :showSections="shouldShowSections"
        :orderedSections="orderedSections"
        :sectionResponses="sectionResponses"
        :sectionSources="sectionSources"
      />
    </FloatingCard>
    <!-- User message -->
    <FloatingCard
      v-else
      class="background-color-secondary-200"
      :withShadow="false"
      :withBorder="false"
    >
      <p>{{ message.text }}</p>
    </FloatingCard>
  </div>
  <ContextualMenuSelection
    :show="showMenu"
    :position="menuPosition"
    :text="selectedText"
    @copy="copyTextToClipboard($event)"
    @insert="insertTextToDocument($event)"
  />
</template>
<script setup lang="ts">
import CollapsibleSections from '@/components/common/collapsible-section/CollapsibleSections.vue';
import BotMessageHeader from '@/components/common/bot-message-header/BotMessageHeader.vue';
import ContextualMenuSelection from '@/components/common/contextual-menu-selection/ContextualMenuSelection.vue';
import FloatingCard from '@/components/common/floating-card/FloatingCard.vue';
import MarkDownComponent from '@/components/common/mark-down/MarkDownComponent.vue';
import ConversationActions from '@/components/common/conversation-actions/ConversationActions.vue';
import { useToasterStore } from '@/stores/useToasterStore';
import { OfficeUtils } from '@/utils/office.utils';
import { onMounted, onUnmounted, ref, computed, VNode } from 'vue';
import { getSectionPriority } from '@/core/sectionPriority';
import { useI18n } from 'vue-i18n';
import { BaseMessage, LegalAssistantSectionsMessage } from '@/models/messages';
import { isValidAnswer } from '@/utils/answer-validation.utils';

interface Props {
  message: BaseMessage;
  botMessageTitle: string;
  hideActions?: boolean;
  botMessageFooter?: (message: BaseMessage) => VNode | null;
  productIndex?: number;
  defaultSection?: string;
}

const props = defineProps<Props>();

onMounted(() => {
  document.addEventListener('selectionchange', handleSelectionChange);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('selectionchange', handleSelectionChange);
  document.removeEventListener('click', handleClickOutside);
});

const { t } = useI18n();
const toaster = useToasterStore();

const showMenu = ref<boolean>(false);
const menuPosition = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const selectedText = ref<string>('');

const sectionResponses = computed(
  () => (props.message as LegalAssistantSectionsMessage).sectionResponses || {}
);
const sectionSources = computed(
  () => (props.message as LegalAssistantSectionsMessage).sectionSources || {}
);

const shouldShowSections = computed(() => props.message.text !== '');
const sectionPriority = computed(() =>
  getSectionPriority(props.productIndex ?? 0)
);
const defaultSection = computed(() => props.defaultSection ?? null);

const orderedSections = computed(() =>
  sectionPriority.value.filter((section: string) => {
    const answer = sectionResponses.value[section];
    if (defaultSection.value && section === defaultSection.value) {
      return false;
    }
    return section in sectionResponses.value && isValidAnswer(answer);
  })
);

function onIconClick(action: 'copy' | 'insert' | 'clip'): void {
  switch (action) {
    case 'copy':
      copyTextToClipboard(props.message.text);
      break;
    case 'insert':
      insertTextToDocument(props.message.text);
      break;
    case 'clip':
      break;
    default:
      console.error('Action not recognized');
  }
}

async function copyTextToClipboard(text: string): Promise<void> {
  try {
    await OfficeUtils.copyTextToClipboard(text);
    toaster.success({
      title: t('actions.toast.text_copied')
    });
  } catch {
    toaster.error({
      title: t('actions.toast.text_copied.error')
    });
  } finally {
    resetContextMenu();
  }
}

async function insertTextToDocument(text: string): Promise<void> {
  try {
    await OfficeUtils.insertTextToDocument(text);
    toaster.success({
      title: t('actions.toast.text_inserted')
    });
  } catch {
    toaster.error({
      title: t('actions.toast.text_inserted.error')
    });
  } finally {
    resetContextMenu();
  }
}

function handleClickOutside(): void {
  const selection = window.getSelection();

  if (!selection || selection.toString().trim() === '') {
    showMenu.value = false;
  }
}

function handleMouseUp(event: MouseEvent): void {
  if (selectedText.value) {
    showMenu.value = true;
    menuPosition.value = { x: event.pageX, y: event.pageY };
  }
}

function handleSelectionChange(): void {
  const selection = window.getSelection();

  if (selection && selection.toString().trim() !== '') {
    selectedText.value = selection.toString();
  } else {
    selectedText.value = '';
    showMenu.value = false;
  }
}

const isAllowedClipboard = (): boolean =>
  Office.context.platform !== Office.PlatformType.OfficeOnline ||
  OfficeUtils.isOutlook();

const resetContextMenu = (): void => {
  if (showMenu.value) {
    showMenu.value = false;
    window.getSelection()?.removeAllRanges();
  }
};
</script>
<style scoped lang="scss">
.card-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: $space-3;
  gap: $space-3;
}

.sources-container {
  margin-top: $space-3;
}
</style>
